// Level two of the two-level control on /platform/ — the screenshot carousel inside six
// of the seven capability sections. Level one, the tab strip that picks the section, is
// modules/feature-tabs.js, and the two modules never speak to each other — see the note
// on sync() for why they do not have to.
//
// Six instances on the page, from three slides to nine. Each owns its own arrows, its own
// rail and its own timer, and none of them can see another's.
//
// Enhancement, not construction, on the same terms as feature-tabs.js: the markup ships
// every slide visible and stacked with the arrows and the rail `hidden`, and this module
// reveals those controls before it hides the first slide.
//
// Not built on Flickity like modules/slider.js. Flickity owns its own DOM and draws its
// own round dots; this wants a segmented rail of labelled buttons sitting in a header row
// next to the arrows, which is markup Flickity would fight. What is borrowed from
// slider.js is the autoplay discipline, for the same reasons noted on each handler.

import IntersectionObsManager from "../managers/IntersectionObserver.js";
import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// The canvas's interval.
const AUTOPLAY_MS = 5000;

// Has to clear the .pf-slide opacity transition in _platform.scss (260ms) rather than
// approximate it — the same pairing, and the same 20ms margin, as feature-tabs.js.
const FADE_MS = 280;

class FeatureCarousel {
    constructor(elem) {
        this.elem = elem;
        this.nav = elem.querySelector("[data-feature-carousel-nav]");
        this.slides = [...elem.querySelectorAll("[data-feature-carousel-slide]")];
        this.segments = [...elem.querySelectorAll("[data-feature-carousel-seg]")];
        this.index = 0;
        // What has been asked for, as opposed to what is on screen. null rather than 0 so
        // the opening show(0) is not mistaken for a repeat of the slide already showing.
        this.target = null;
        this.autoplay = null;
        this.fadeTimer = null;
        this.autoplayEnded = false;
        this.onScreen = false;

        // A rail segment per slide, or the rail is reporting a position that does not
        // exist. As in feature-tabs.js, the safe failure is to leave the stacked markup
        // alone rather than hide slides this module cannot then index.
        if (this.slides.length < 2 || this.segments.length !== this.slides.length) return;

        this.reveal();
        this.bind();
        this.show(0, { animate: false });
        this.watch();
    }

    // Controls first, then the class that hides slides — so there is never a frame with
    // one slide showing and no way to reach the other eight.
    reveal() {
        this.nav.hidden = false;
        this.elem.classList.add("is-enhanced");
    }

    bind() {
        this.elem.querySelector("[data-feature-carousel-prev]").addEventListener("click", () => {
            this.endAutoplay();
            this.step(-1);
        });

        this.elem.querySelector("[data-feature-carousel-next]").addEventListener("click", () => {
            this.endAutoplay();
            this.step(1);
        });

        // The rail is a control here, not the progress indicator the same-looking rail on
        // /developer-program/ is. There, three labelled chips sit above it and give direct
        // access to all three panels, so a clickable rail would have been a second set of
        // controls in the accessibility tree. Here there are no chips and up to nine
        // slides, so without these the ninth screen is eight clicks away.
        this.segments.forEach((segment, i) => {
            segment.addEventListener("click", () => {
                this.endAutoplay();
                this.show(i);
            });
        });

        // Focus is an interaction, not just clicks — and here it is the one that matters
        // most. Inactive slides are hidden with `visibility`, which takes them out of the
        // tab order, so a visitor who tabs onto a control inside the live slide and waits
        // has the timer pull that slide out from under them: swap() sets `is-inactive` on
        // the element holding focus and focus falls to <body>. Measured at 6.5s after a
        // Tab onto a screenshot link, activeElement went from the link to BODY.
        //
        // One listener on the carousel rather than per control: it covers the arrows, the
        // rail and the screenshot links, including any added later. It also subsumes the
        // click handler the zoom links used to carry — clicking a link focuses it, so the
        // focusin fires either way, which is why that handler is gone rather than kept
        // alongside this one.
        this.elem.addEventListener("focusin", () => this.endAutoplay());

        onReducedMotionChange(this.handleMotionPreference);
    }

    watch() {
        // A preference already set at load fires no `change` event, so handleMotionPreference
        // can never run and autoplayEnded would never flip — leaving an observer registered
        // for the life of the page on a timer that is provably unable to start. Retiring it
        // up front matches the one-way policy on that handler rather than bending it.
        if (prefersReducedMotion()) {
            this.autoplayEnded = true;
            return;
        }

        this.observer = new IntersectionObsManager();
        this.observer.observe(this.elem, this.handleIntersection);
    }

    handleIntersection = (entry) => {
        this.onScreen = entry.isIntersecting;
        this.sync();
    };

    // The single place the timer is started or stopped. Everything else changes one input
    // and calls this, so there is no path that can leave a timer running in a panel the
    // visitor has tabbed away from — which is the bug the canvas has, where all six
    // timers start on mount and never stop.
    //
    // `onScreen` covers both reasons to be off-duty, and that is not an accident:
    // feature-tabs.js hides an inactive panel with `display: none`, which zeroes its
    // intersection rectangle, so a carousel in a panel the visitor has tabbed away from
    // reports exactly as one scrolled past. Were the panels hidden with `visibility`
    // instead — as the slides below are — they would keep their rectangle and five of the
    // six carousels would rotate forever behind the visible one.
    sync() {
        const shouldRun = this.onScreen && !this.autoplayEnded && !prefersReducedMotion();

        if (shouldRun) {
            // Offscreen and off-duty both stop the timer rather than pausing it. A slide
            // rotating where nobody can see it is wasted, and worse, it means a visitor
            // coming back to a section finds a screen they never advanced to.
            if (!this.autoplay) this.autoplay = setInterval(() => this.step(1), AUTOPLAY_MS);
        } else {
            clearInterval(this.autoplay);
            this.autoplay = null;
        }
    }

    // Narrower than "any interaction", the same call slider.js and use-cases.js both
    // make: the visitor taking the wheel retires the timer for the session, but merely
    // scrolling past, or switching tabs and coming back, does not.
    endAutoplay() {
        if (this.autoplayEnded) return;

        this.autoplayEnded = true;
        this.sync();
        if (this.observer) this.observer.unobserve(this.elem);
    }

    // One-way, as in slider.js. The preference turning on has to stop a rotation already
    // running; turning it back off deliberately does not restart one, because a visitor
    // who has been reading a static screenshot should not have it start moving.
    handleMotionPreference = () => {
        if (prefersReducedMotion()) this.endAutoplay();
    };

    // Counts from `target`, not `index`. Counting from what is on screen meant two quick
    // taps on Next both resolved to the same slide — the second landed on a step the
    // first had already claimed, and the pair advanced one place instead of two.
    step(delta) {
        const from = this.target ?? this.index;

        this.show((from + delta + this.slides.length) % this.slides.length);
    }

    show(next, { animate = true } = {}) {
        // `target` is the slide asked for, `index` the one on screen; they differ for the
        // length of a fade. See step() above for what guarding on the wrong one cost.
        if (next === this.target) return;

        this.target = next;
        clearTimeout(this.fadeTimer);

        const swap = () => {
            this.slides.forEach((slide, i) => {
                // `visibility` via a class, not `hidden`, for the same reason as the
                // panels one level up: the inactive slides have to keep their layout
                // boxes so the grid stack in _platform.scss sizes the frame to the
                // tallest of them and the page does not jump on every advance. Nine
                // slides of quite different copy lengths makes that very visible here.
                slide.classList.toggle("is-inactive", i !== next);
                slide.classList.remove("is-fading");
            });

            this.segments.forEach((segment, i) => {
                segment.classList.toggle("is-active", i === next);

                // The rail reports position as well as offering the jump, and a visitor
                // using a screen reader needs that reported rather than drawn. Written
                // only to the two segments whose value actually changes: `classList.toggle`
                // is short-circuited internally when the class string is unchanged, but an
                // identical `setAttribute` still runs the attribute-changed path and
                // touches the accessibility tree — nine times an advance, seven of them
                // for nothing, in section 06.
                if (i === next || i === this.index) segment.setAttribute("aria-current", String(i === next));
            });

            this.index = next;
        };

        // `next === this.index` is the cancel case: stepping back onto the slide already
        // showing, which undoes a fade in flight rather than starting a new one. swap()
        // clears is-fading and re-asserts the rail state the cancelled swap would have
        // left pointing at the wrong segment.
        if (!animate || prefersReducedMotion() || next === this.index) {
            swap();
            return;
        }

        this.slides[this.index].classList.add("is-fading");
        this.fadeTimer = setTimeout(swap, FADE_MS);
    }
}

document.querySelectorAll("[data-feature-carousel]").forEach((elem) => new FeatureCarousel(elem));
